import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./Dialog.module.scss";
import { Button } from "../Button/Button";
import TimesIcon from "../../icons/Times";
import { cn } from "../../../helpers";

export interface DialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    variant?: "center" | "drawer";
    showFooter?: boolean;
    onSave?: () => void;
    onCancel?: () => void;
    saveText?: string;
    cancelText?: string;
    isSubmitBtnDisabled?: boolean;
    isCancelBtnDisabled?: boolean;
    wrapperClassName?: string;
    overlayClassName?: string;
    submitBtnFormValue?: string;
    isDeleting?: boolean;
    backBtnText?: string;
    onBackBtnClick?: () => void;
    isBackBtnDisabled?: boolean;
    ariaLabel?: string;
    /** Opt in for coordinated modal focus; existing dialog behaviour is unchanged. */
    focusManagement?: { paused?: boolean; portalId?: string };
}

export const Dialog: React.FC<DialogProps> = ({
    open,
    onClose,
    title,
    children,
    variant = "center",
    showFooter = true,
    onSave,
    onCancel,
    saveText = "Save",
    cancelText = "Cancel",
    isSubmitBtnDisabled,
    isCancelBtnDisabled,
    wrapperClassName,
    overlayClassName,
    submitBtnFormValue,
    isDeleting,
    backBtnText,
    onBackBtnClick,
    isBackBtnDisabled,
    ariaLabel,
    focusManagement,
}) => {
    const shouldShowFooter = variant === "drawer" ? false : showFooter;
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const managesFocus = Boolean(focusManagement);
    const focusPaused = Boolean(focusManagement?.paused);
    const portalId = focusManagement?.portalId;

    useEffect(() => {
        if (!open || !managesFocus || focusPaused) return;
        // Let a nested dialog pause its parent's boundary before receiving focus.
        const frame = requestAnimationFrame(() => {
            if (!contentRef.current?.contains(document.activeElement)) {
                contentRef.current?.focus();
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [open, managesFocus, focusPaused]);

    React.useLayoutEffect(() => {
        if (!open || !managesFocus || focusPaused) return;
        const roots = () =>
            [
                contentRef.current,
                portalId ? document.getElementById(portalId) : null,
            ].filter((root): root is HTMLElement => Boolean(root));
        const contains = (target: EventTarget | null) =>
            target instanceof Node && roots().some((root) => root.contains(target));
        const focusable = () =>
            roots().flatMap((root) =>
                Array.from(
                    root.querySelectorAll<HTMLElement>(
                        'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    )
                ).filter(
                    (element) =>
                        !element.closest('[hidden], [aria-hidden="true"]') &&
                        element.tabIndex >= 0
                )
            );
        const keepFocus = (event: FocusEvent) => {
            if (!contains(event.target)) contentRef.current?.focus();
        };
        const wrapTab = (event: KeyboardEvent) => {
            if (event.key === "Escape" && event.defaultPrevented) {
                // A calendar portal can remove its focused day without firing focusin.
                const content = contentRef.current;
                requestAnimationFrame(() => {
                    if (content?.isConnected && !contains(document.activeElement)) {
                        content.focus();
                    }
                });
                return;
            }
            if (event.key !== "Tab" || event.defaultPrevented) return;
            const elements = focusable();
            const first = elements[0];
            const last = elements[elements.length - 1];
            const active = document.activeElement;
            if (!first) {
                event.preventDefault();
                contentRef.current?.focus();
            } else if (
                event.shiftKey &&
                (active === first ||
                    active === contentRef.current ||
                    !contains(active))
            ) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || !contains(active))) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("focusin", keepFocus);
        window.addEventListener("keydown", wrapTab);
        return () => {
            document.removeEventListener("focusin", keepFocus);
            window.removeEventListener("keydown", wrapTab);
        };
    }, [open, managesFocus, focusPaused, portalId]);

    useEffect(() => {
        if (open) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = "hidden";

            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [open]);

    // Close on Escape key
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && (!managesFocus || !e.defaultPrevented))
                onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose, managesFocus]);

    // Close on click outside
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === overlayRef.current) {
            onClose();
        }
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            className={cn(styles.overlay, overlayClassName)}
            ref={overlayRef}
            onClick={handleOverlayClick}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
            aria-label={ariaLabel}
        >
            <div
                className={cn(
                    wrapperClassName,
                    variant === "center"
                        ? styles.centerContent
                        : styles.drawerContent
                )}
                tabIndex={managesFocus ? -1 : 0}
                ref={contentRef}
            >
                <div className={styles.header}>
                    {title && <div className={styles.title}>{title}</div>}
                    <Button
                        size="icon"
                        variant="normal"
                        type="button"
                        title="Close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <TimesIcon />
                    </Button>
                </div>
                <div
                    className={`${styles.body} ${
                        !shouldShowFooter ? styles.bodyNoFooter : ""
                    }`}
                >
                    {children}
                </div>
                {shouldShowFooter ? (
                    <div className={styles.footer}>
                        {backBtnText && (
                            <Button
                                variant="secondary"
                                className={styles.backBtn}
                                onClick={onBackBtnClick}
                                disabled={isBackBtnDisabled}
                            >
                                {backBtnText}
                            </Button>
                        )}

                        <Button
                            variant="secondary"
                            onClick={onCancel || onClose}
                            disabled={isCancelBtnDisabled}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            variant={isDeleting ? "danger" : "primary"}
                            onClick={onSave}
                            type="submit"
                            disabled={isSubmitBtnDisabled}
                            form={submitBtnFormValue}
                        >
                            {saveText}
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>,
        document.body
    );
};
